/* useUserSync — generic localStorage ⇄ server sync hook for logged-in users.
   - On mount (and on auth change): pull server data, merge with local, write merged
     to BOTH local and server. After this, local is the source of truth for the session.
   - Whenever local data changes (debounced 800ms), push to server in the background.
   - When logged out, this hook is a no-op (local-only behavior preserved).

   Usage:
     const { ready } = useUserSync({
       scope: "workouts",                        // or "food"
       readLocal: () => ({...}),                  // build current local snapshot
       writeLocal: (merged) => { ...setStates },  // apply server data into local state
       merge: (server, local) => merged,          // pure merge fn
       deps: [log, customFoods, ...],             // values to watch for push
     });
*/
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useUserSync({ scope, readLocal, writeLocal, merge, deps = [] }) {
  const { user } = useAuth();
  const isAuthed = !!(user && user !== false);
  const [ready, setReady] = useState(!isAuthed);
  const initialPullDone = useRef(false);
  const pushTimer = useRef(null);

  // Pull on auth
  useEffect(() => {
    if (!isAuthed) {
      setReady(true);
      initialPullDone.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: serverData } = await axios.get(`${API}/sync/${scope}`);
        const localSnap = readLocal();
        const merged = merge(serverData || {}, localSnap || {});
        writeLocal(merged);
        // Push merged back so server has the union
        await axios.post(`${API}/sync/${scope}`, { data: merged });
        if (!cancelled) {
          initialPullDone.current = true;
          setReady(true);
        }
      } catch {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  // Debounced push on local changes (only after initial pull)
  useEffect(() => {
    if (!isAuthed || !initialPullDone.current) return;
    if (pushTimer.current) clearTimeout(pushTimer.current);
    pushTimer.current = setTimeout(() => {
      const snap = readLocal();
      axios.post(`${API}/sync/${scope}`, { data: snap }).catch(() => {});
    }, 800);
    return () => {
      if (pushTimer.current) clearTimeout(pushTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ready };
}
